import { useCallback, useContext } from 'react';
import useUserRole from './useUserRole';
import { AuthContext } from '../contexts/AuthContext';

// Define permission types
export type Permission = 
  // Project permissions
  | 'view_projects' 
  | 'create_project' 
  | 'edit_project' 
  | 'delete_project'
  // Schedule permissions
  | 'view_schedules'
  | 'create_schedule'
  | 'edit_schedule'
  | 'delete_schedule'
  | 'set_baseline'
  // Team permissions
  | 'view_team'
  | 'manage_team'
  | 'assign_tasks'
  // Report permissions
  | 'view_reports'
  | 'create_report'
  | 'share_report'
  // Insights / Analytics (gated separately so report_viewer can be restricted)
  | 'view_insights'
  | 'view_analytics'
  // Work package permissions
  | 'view_work_packages'
  | 'create_work_package'
  | 'edit_work_package'
  | 'delete_work_package'
  // Organization permissions
  | 'view_organization'
  | 'edit_organization'
  | 'manage_organization_users'
  // Admin permissions
  | 'access_admin_panel'
  | 'manage_users'
  | 'manage_roles'
  | 'system_settings';

// Define feature access types
export type Feature =
  | 'ai_insights'
  | 'schedule_optimization'
  | 'resource_management'
  | 'cost_control'
  | 'risk_analysis'
  | 'gantt_chart'
  | 'advanced_reporting'
  | 'what_if_analysis'
  | 'subcontractor_portal'
  | 'team_collaboration';

// Define role-based permissions
const ROLE_PERMISSIONS: Record<string, Permission[]> = {
  admin: [
    'view_projects', 'create_project', 'edit_project', 'delete_project',
    'view_schedules', 'create_schedule', 'edit_schedule', 'delete_schedule', 'set_baseline',
    'view_team', 'manage_team', 'assign_tasks',
    'view_reports', 'create_report', 'share_report',
    'view_insights', 'view_analytics',
    'view_work_packages', 'create_work_package', 'edit_work_package', 'delete_work_package',
    'view_organization', 'edit_organization', 'manage_organization_users',
    'access_admin_panel', 'manage_users', 'manage_roles', 'system_settings'
  ],
  organization_admin: [
    'view_projects', 'create_project', 'edit_project', 'delete_project',
    'view_schedules', 'create_schedule', 'edit_schedule', 'delete_schedule', 'set_baseline',
    'view_team', 'manage_team', 'assign_tasks',
    'view_reports', 'create_report', 'share_report',
    'view_insights', 'view_analytics',
    'view_work_packages', 'create_work_package', 'edit_work_package', 'delete_work_package',
    'view_organization', 'edit_organization', 'manage_organization_users'
  ],
  project_manager: [
    'view_projects', 'create_project', 'edit_project',
    'view_schedules', 'create_schedule', 'edit_schedule', 'set_baseline',
    'view_team', 'manage_team', 'assign_tasks',
    'view_reports', 'create_report', 'share_report',
    'view_insights', 'view_analytics',
    'view_work_packages', 'create_work_package', 'edit_work_package', 'delete_work_package'
  ],
  scheduler: [
    'view_projects',
    'view_schedules', 'create_schedule', 'edit_schedule', 'set_baseline',
    'view_team',
    'view_reports', 'create_report',
    'view_insights', 'view_analytics',
    'view_work_packages'
  ],
  field_engineer: [
    'view_projects',
    'view_schedules',
    'view_team',
    'view_reports', 'create_report',
    'view_insights', 'view_analytics',
    'view_work_packages', 'edit_work_package'
  ],
  subcontractor: [
    'view_projects',
    'view_schedules',
    'view_work_packages', 'edit_work_package'
  ],
  /** Reports and Activities only (e.g. AuthKit user with this role sees only those pages). */
  report_viewer: [
    'view_projects',
    'view_reports', 'create_report',
  ],
  /** Default for DB role "user" when no other role is assigned. */
  user: [
    'view_projects',
  ],
};

// Define role-based feature access
const ROLE_FEATURES: Record<string, Feature[]> = {
  admin: [
    'ai_insights', 'schedule_optimization', 'resource_management', 
    'cost_control', 'risk_analysis', 'gantt_chart', 
    'advanced_reporting', 'what_if_analysis', 
    'subcontractor_portal', 'team_collaboration'
  ],
  organization_admin: [
    'ai_insights', 'schedule_optimization', 'resource_management', 
    'cost_control', 'risk_analysis', 'gantt_chart', 
    'advanced_reporting', 'what_if_analysis', 
    'subcontractor_portal', 'team_collaboration'
  ],
  project_manager: [
    'ai_insights', 'schedule_optimization', 'resource_management', 
    'cost_control', 'risk_analysis', 'gantt_chart', 
    'advanced_reporting', 'team_collaboration'
  ],
  scheduler: [
    'ai_insights', 'schedule_optimization', 'resource_management', 
    'gantt_chart', 'what_if_analysis'
  ],
  field_engineer: [
    'gantt_chart', 'team_collaboration'
  ],
  subcontractor: [
    'subcontractor_portal'
  ]
};

// Define permission descriptions
export const PERMISSION_DESCRIPTIONS: Record<Permission, string> = {
  view_projects: 'View project details and listings',
  create_project: 'Create new projects',
  edit_project: 'Edit project details',
  delete_project: 'Delete projects',
  view_schedules: 'View project schedules',
  create_schedule: 'Create new schedules',
  edit_schedule: 'Edit existing schedules',
  delete_schedule: 'Delete schedules',
  set_baseline: 'Set baseline schedules',
  view_team: 'View team members',
  manage_team: 'Manage team members and assignments',
  assign_tasks: 'Assign tasks to team members',
  view_reports: 'View reports',
  create_report: 'Create new reports',
  share_report: 'Share reports with others',
  view_insights: 'View insights',
  view_analytics: 'View analytics',
  view_work_packages: 'View work packages',
  create_work_package: 'Create new work packages',
  edit_work_package: 'Edit work packages',
  delete_work_package: 'Delete work packages',
  view_organization: 'View organization details',
  edit_organization: 'Edit organization details',
  manage_organization_users: 'Manage organization users',
  access_admin_panel: 'Access admin panel',
  manage_users: 'Manage system users',
  manage_roles: 'Manage user roles',
  system_settings: 'Modify system settings'
};

// Define feature descriptions
export const FEATURE_DESCRIPTIONS: Record<Feature, string> = {
  ai_insights: 'AI-powered schedule insights and recommendations',
  schedule_optimization: 'Automatic schedule optimization',
  resource_management: 'Resource allocation and management',
  cost_control: 'Budget and cost control features',
  risk_analysis: 'Project risk analysis and mitigation',
  gantt_chart: 'Interactive Gantt chart visualization',
  advanced_reporting: 'Advanced reporting and analytics',
  what_if_analysis: 'What-if scenario analysis',
  subcontractor_portal: 'Subcontractor portal access',
  team_collaboration: 'Team collaboration tools'
};

/**
 * Custom hook for checking user permissions and feature access based on their role.
 * When authenticated via AuthContext (cookie/session), grants full access (no RBAC).
 */
const usePermission = () => {
  const { role } = useUserRole();
  const auth = useContext(AuthContext);

  // Cookie/session auth: single role = full access
  const fullAccess = auth?.isAuthenticated ?? false;

  // Check if user has a specific permission
  const hasPermission = useCallback((permission: Permission | Permission[]): boolean => {
    if (fullAccess) return true;
    if (!role || !ROLE_PERMISSIONS[role]) {
      return false;
    }
    if (Array.isArray(permission)) {
      return permission.some(p => ROLE_PERMISSIONS[role].includes(p));
    }
    return ROLE_PERMISSIONS[role].includes(permission);
  }, [role, fullAccess]);
  
  // Check if user has all of the specified permissions
  const hasAllPermissions = useCallback((permissions: Permission[]): boolean => {
    if (fullAccess) return true;
    if (!role || !ROLE_PERMISSIONS[role]) {
      return false;
    }
    return permissions.every(permission => ROLE_PERMISSIONS[role].includes(permission));
  }, [role, fullAccess]);
  
  // Check if user has access to a specific feature
  const hasFeatureAccess = useCallback((feature: Feature | Feature[]): boolean => {
    if (fullAccess) return true;
    if (!role || !ROLE_FEATURES[role]) {
      return false;
    }
    if (Array.isArray(feature)) {
      return feature.some(f => ROLE_FEATURES[role].includes(f));
    }
    return ROLE_FEATURES[role].includes(feature);
  }, [role, fullAccess]);
  
  // Get all permissions for the current role
  const getRolePermissions = useCallback((): Permission[] => {
    if (fullAccess) return Object.keys(PERMISSION_DESCRIPTIONS) as Permission[];
    if (!role || !ROLE_PERMISSIONS[role]) {
      return [];
    }
    return ROLE_PERMISSIONS[role];
  }, [role, fullAccess]);
  
  // Get all features for the current role
  const getRoleFeatures = useCallback((): Feature[] => {
    if (fullAccess) return Object.keys(FEATURE_DESCRIPTIONS) as Feature[];
    if (!role || !ROLE_FEATURES[role]) {
      return [];
    }
    return ROLE_FEATURES[role];
  }, [role, fullAccess]);
  
  // Check if user has any of the specified permissions
  const hasAnyPermission = useCallback((permissions: Permission[]): boolean => {
    if (fullAccess) return true;
    if (!role || !ROLE_PERMISSIONS[role]) {
      return false;
    }
    return permissions.some(permission => ROLE_PERMISSIONS[role].includes(permission));
  }, [role, fullAccess]);
  
  return { 
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    hasFeatureAccess,
    getRolePermissions,
    getRoleFeatures
  };
};

export default usePermission; 